import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SessionAction } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Button, Typography } from '@/ui';

import type { Session } from '@/types';

import { MemberCancelSession } from '@/components/MentoCal/MemberCancelSession';
import { MemberRescheduleSession } from '@/components/MentoCal/MemberRescheduleSession';
import { appointmentActionSession } from '@/features/appointments/appointmentActionSession';
import {
  type AppointmentParticipantRole,
  resolveAppointmentParticipantRole,
} from '@/features/appointments/appointmentParticipantRole';
import {
  canCancelSession,
  canRescheduleSession,
} from '@/features/appointments/sessionCapabilities';
import {
  storedAppointmentLifecycleRecovery,
  type StoredAppointmentLifecycleRecovery,
} from '@/features/appointments/useAppointmentLifecycleGuard';
import CoachCancelSessionDialog from '@/features/coachSchedule/CoachCancelSessionDialog';
import CoachRescheduleSession from '@/features/coachSchedule/CoachRescheduleSession';
import { useGetAppointmentQuery } from '@/store/api/appointmentApi';
import { useGetMomentsApiSessionReadinessQuery } from '@/store/api/momentsApi';
import {
  useGetMomentsParticipantAppointmentQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';

const statusFromError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return undefined;
  }
  const { status } = error as { status?: unknown };
  return typeof status === 'number' ? status : undefined;
};

const cancellationRecoverySession = (
  appointmentId: string,
  recovery: StoredAppointmentLifecycleRecovery,
): Session | undefined => {
  try {
    const command = JSON.parse(recovery.bytes) as {
      appointmentId?: unknown;
      expectedSeriesMutationVersion?: unknown;
      expectedVersion?: unknown;
      seriesId?: unknown;
    };
    const validFence =
      (command.seriesId === null &&
        command.expectedSeriesMutationVersion === null) ||
      (typeof command.seriesId === 'string' &&
        command.seriesId.length > 0 &&
        Number.isInteger(command.expectedSeriesMutationVersion) &&
        (command.expectedSeriesMutationVersion as number) > 0);
    if (
      command.appointmentId !== appointmentId ||
      !Number.isInteger(command.expectedVersion) ||
      (command.expectedVersion as number) <= 0 ||
      !validFence
    ) {
      return undefined;
    }
    const seriesId =
      typeof command.seriesId === 'string' ? command.seriesId : null;
    const seriesMutationVersion =
      typeof command.expectedSeriesMutationVersion === 'number'
        ? command.expectedSeriesMutationVersion
        : 0;
    return {
      bookId: `moments:${appointmentId}`,
      bookingSource: 'moments',
      coach: { fullName: '' },
      coachId: '',
      createdAt: '9999-01-01T00:00:00.000Z',
      endedAt: '9999-01-01T01:00:00.000Z',
      id: appointmentId,
      isChemistryCall: false,
      kind: seriesId === null ? 'single' : 'biweekly',
      recurringId: seriesId ?? '',
      slug: 'coaching',
      sourceAppointmentCancelAllowed: true,
      sourceAppointmentCancelReasonRequired: false,
      sourceAppointmentCancelScopes: ['appointment'],
      sourceAppointmentId: appointmentId,
      sourceAppointmentIdentityScheme: 'moments-person',
      sourceAppointmentRescheduleAllowed: false,
      sourceAppointmentRescheduleScopes: [],
      sourceAppointmentSeriesId: seriesId,
      sourceAppointmentSeriesMutationVersion: seriesMutationVersion,
      sourceAppointmentVersion: command.expectedVersion,
      startedAt: '9999-01-01T00:00:00.000Z',
      status: 'active',
      title: 'Coaching Session',
      user: { fullName: '' },
      userId: '',
    } as unknown as Session;
  } catch {
    return undefined;
  }
};

const ExternalAppointmentAction = () => {
  const { appointmentId = '', action } = useParams();
  const { user: currentUser, isUserLoading } = useCurrentUser();
  const [nativeActionOverride, setNativeActionOverride] = useState<{
    appointmentId: string;
    routeAction: string | undefined;
    value: string;
  }>();
  const nativeAction =
    nativeActionOverride?.appointmentId === appointmentId &&
    nativeActionOverride.routeAction === action
      ? nativeActionOverride.value
      : action;
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false);
  const supportedAction =
    action === SessionAction.CANCEL || action === SessionAction.RESCHEDULE;
  const cancellationAction = action === SessionAction.CANCEL;
  const memberRecovery = cancellationAction
    ? storedAppointmentLifecycleRecovery(
        `member-cancel:${appointmentId}:${appointmentId}`,
        'rails',
      )
    : undefined;
  const coachRecovery = cancellationAction
    ? storedAppointmentLifecycleRecovery(
        `coach-cancel:coach:${appointmentId}:${appointmentId}`,
        'rails',
      )
    : undefined;
  const recoveryConflict = Boolean(memberRecovery && coachRecovery);
  let recovery:
    | {
        record: StoredAppointmentLifecycleRecovery;
        role: AppointmentParticipantRole;
      }
    | undefined;
  if (memberRecovery && !coachRecovery) {
    recovery = { record: memberRecovery, role: 'member' };
  } else if (coachRecovery && !memberRecovery) {
    recovery = { record: coachRecovery, role: 'coach' };
  }
  const recoveredSession = recovery
    ? cancellationRecoverySession(appointmentId, recovery.record)
    : undefined;
  const [establishedRoute, setEstablishedRoute] = useState<{
    action: string | undefined;
    appointmentId: string;
    transport: 'moments' | 'rails';
  }>();
  const establishedTransport =
    recovery?.record.transport ??
    (establishedRoute?.appointmentId === appointmentId &&
    establishedRoute.action === action
      ? establishedRoute.transport
      : undefined);
  const canLoad =
    !isUserLoading &&
    Boolean(currentUser.id) &&
    Boolean(appointmentId) &&
    supportedAction;
  const participantAppointmentQuery = useGetMomentsParticipantAppointmentQuery(
    { appointmentId },
    {
      refetchOnMountOrArgChange: true,
      skip:
        !canLoad ||
        !cancellationAction ||
        Boolean(memberRecovery || coachRecovery),
    },
  );
  const routeDecision = useParticipantAppointmentRouteDecision({
    coachExternalId: cancellationAction
      ? participantAppointmentQuery.currentData?.coachSelector.externalId
      : undefined,
    establishedTransport,
    flow: 'cancellation',
  });
  const cancellationTransport =
    establishedTransport ??
    (routeDecision.state === 'ready' ? routeDecision.transport : undefined);
  const useDirectParticipantTransport =
    cancellationAction && cancellationTransport === 'moments';
  useEffect(() => {
    if (
      cancellationAction &&
      !establishedTransport &&
      routeDecision.state === 'ready'
    ) {
      setEstablishedRoute({
        action,
        appointmentId,
        transport: routeDecision.transport,
      });
    }
  }, [
    action,
    appointmentId,
    cancellationAction,
    establishedTransport,
    routeDecision,
  ]);
  const railsAppointmentQuery = useGetAppointmentQuery(
    { appointmentId, userId: currentUser.id },
    {
      refetchOnMountOrArgChange: true,
      skip:
        !canLoad ||
        recoveryConflict ||
        (cancellationAction && cancellationTransport !== 'rails'),
    },
  );
  const {
    currentData: appointment,
    error,
    isFetching,
    isLoading,
    refetch,
  } = useDirectParticipantTransport
    ? participantAppointmentQuery
    : railsAppointmentQuery;
  const {
    currentData: readiness,
    error: readinessError,
    isFetching: isReadinessFetching,
    isLoading: isReadinessLoading,
    refetch: refetchReadiness,
  } = useGetMomentsApiSessionReadinessQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: !canLoad,
  });

  useEffect(() => {
    hasNavigatedRef.current = false;
    setNativeActionOverride(undefined);
  }, [action, appointmentId]);

  const navigateOnce = (
    role: AppointmentParticipantRole,
    cancelled = false,
  ) => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    if (role === 'coach') {
      navigate('/coach/schedule', { replace: true });
      return;
    }
    navigate(
      '/m/sessions/upcoming',
      cancelled
        ? { replace: true, state: { sessionCancelled: true } }
        : { replace: true },
    );
  };

  const participantRole =
    appointment && readiness?.person?.id
      ? resolveAppointmentParticipantRole(appointment, readiness.person.id)
      : null;

  useEffect(() => {
    if (
      error ||
      readinessError ||
      isUserLoading ||
      isLoading ||
      isFetching ||
      isReadinessLoading ||
      isReadinessFetching ||
      !participantRole ||
      (appointment?.lifecycleStatus !== 'cancelled' &&
        appointment?.lifecycleStatus !== 'cancellation_pending')
    ) {
      return;
    }
    navigateOnce(participantRole, true);
  }, [
    appointment?.lifecycleStatus,
    error,
    isFetching,
    isLoading,
    isReadinessFetching,
    isReadinessLoading,
    isUserLoading,
    participantRole,
    readinessError,
  ]);

  if (recoveryConflict) {
    return <Typography>Unable to restore saved cancellation</Typography>;
  }

  if (cancellationAction && recovery && recoveredSession) {
    const close = (cancelled = false) => navigateOnce(recovery.role, cancelled);
    if (recovery.role === 'coach') {
      return (
        <CoachCancelSessionDialog
          close={close}
          participantAppointmentTransport={recovery.record.transport}
          session={recoveredSession}
        />
      );
    }
    return (
      <MemberCancelSession
        close={close}
        participantAppointmentTransport={recovery.record.transport}
        session={recoveredSession}
        userId={currentUser.id}
        onCancelled={() => close(true)}
      />
    );
  }

  if (!supportedAction) {
    return <Typography>Unknown appointment action</Typography>;
  }

  if (
    !appointmentId ||
    isUserLoading ||
    (cancellationAction &&
      (participantAppointmentQuery.isLoading ||
        participantAppointmentQuery.isFetching ||
        (!establishedTransport && routeDecision.state === 'loading'))) ||
    isLoading ||
    isFetching ||
    isReadinessLoading ||
    isReadinessFetching
  ) {
    return <Typography>Loading appointment...</Typography>;
  }

  if (
    cancellationAction &&
    (participantAppointmentQuery.error ||
      (!establishedTransport && routeDecision.state === 'unavailable'))
  ) {
    const participantErrorStatus = statusFromError(
      participantAppointmentQuery.error,
    );
    if (participantErrorStatus === 403) {
      return (
        <Typography>You do not have access to this appointment</Typography>
      );
    }
    if (participantErrorStatus === 404) {
      return <Typography>Appointment not found</Typography>;
    }
    return <Typography>Unable to load appointment</Typography>;
  }

  const errorStatus = statusFromError(error);
  if (errorStatus === 403) {
    return <Typography>You do not have access to this appointment</Typography>;
  }

  if (errorStatus === 404) {
    return <Typography>Appointment not found</Typography>;
  }

  if (error) {
    return (
      <div>
        <Typography>Unable to load appointment</Typography>
        <Button variant="outlined" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  if (readinessError || !readiness?.person?.id) {
    return (
      <div>
        <Typography>Unable to verify appointment participant.</Typography>
        <Button variant="outlined" onClick={refetchReadiness}>
          Retry
        </Button>
      </div>
    );
  }

  if (!appointment) {
    return <Typography>Appointment not found</Typography>;
  }

  if (!participantRole) {
    return <Typography>You do not have access to this appointment</Typography>;
  }

  if (
    appointment.lifecycleStatus === 'cancelled' ||
    appointment.lifecycleStatus === 'cancellation_pending'
  ) {
    return <Typography>Redirecting to schedule...</Typography>;
  }

  const session = appointmentActionSession(appointment);
  const close = (cancelled = false) => navigateOnce(participantRole, cancelled);

  if (
    nativeAction === SessionAction.RESCHEDULE &&
    canRescheduleSession(session)
  ) {
    if (participantRole === 'coach') {
      return <CoachRescheduleSession close={close} session={session} />;
    }
    return (
      <MemberRescheduleSession
        close={close}
        session={session}
        onRescheduled={close}
      />
    );
  }

  if (nativeAction === SessionAction.CANCEL && canCancelSession(session)) {
    if (participantRole === 'coach') {
      return (
        <CoachCancelSessionDialog
          close={close}
          participantAppointmentTransport={cancellationTransport}
          session={session}
        />
      );
    }
    return (
      <MemberCancelSession
        close={close}
        participantAppointmentTransport={cancellationTransport}
        session={session}
        userId={currentUser.id}
        onCancelled={() => close(true)}
        onRescheduleInstead={
          session.sourceAppointmentRescheduleAllowed
            ? () =>
                setNativeActionOverride({
                  appointmentId,
                  routeAction: action,
                  value: SessionAction.RESCHEDULE,
                })
            : undefined
        }
      />
    );
  }

  return (
    <Typography>
      This appointment can no longer be{' '}
      {nativeAction === SessionAction.CANCEL ? 'cancelled' : 'rescheduled'}
    </Typography>
  );
};

export default ExternalAppointmentAction;
